import { consola } from 'consola'
import { useAdminSessionManager } from '../model/useAdminSessionManager'
import { useAdminTokenRefresh } from '../model/useAdminTokenRefresh'
import { useTokenRefreshV2 } from '../model/useTokenRefreshV2'
import { useSessionManager } from '@entities/auth'
import { setApiTokenBridge, type ApiTokenBridge } from '@shared/model'

const logger = consola.withTag('api-token-bridge')

function createBridge(): ApiTokenBridge {
  return {
    async handleUnauthorized({ isAdminRequest }) {
      try {
        if (isAdminRequest) {
          const adminRefresh = useAdminTokenRefresh()
          const adminSession = useAdminSessionManager()

          if (!adminSession.getAdminRefreshToken()) return false

          if (adminRefresh.isRefreshing.value) {
            return !!(await adminRefresh.waitForAdminTokenRefresh())
          }

          return !!(await adminRefresh.refreshAdminToken())
        }

        const tokenRefresh = useTokenRefreshV2()
        const session = useSessionManager()

        if (!session.authRefreshToken.value) return false

        if (tokenRefresh.isRefreshing.value) {
          return !!(await tokenRefresh.waitForTokenRefresh())
        }

        return !!(await tokenRefresh.refreshToken())
      } catch (error) {
        logger.error('Token refresh failed:', error)
        return false
      }
    },

    async proactiveBeforeRequest({ url }) {
      try {
        const urlIsAdmin = url.startsWith('/admin/')

        if (urlIsAdmin) {
          const adminSession = useAdminSessionManager()

          await adminSession.waitForInit()

          if (adminSession.shouldRefreshAdminToken()) {
            const adminRefresh = useAdminTokenRefresh()

            await (adminRefresh.isRefreshing.value ? adminRefresh.waitForAdminTokenRefresh() : adminRefresh.refreshAdminToken())
          }
          return
        }

        const session = useSessionManager()

        if (session.shouldRefreshToken()) {
          const tokenRefresh = useTokenRefreshV2()

          await (tokenRefresh.isRefreshing.value ? tokenRefresh.waitForTokenRefresh() : tokenRefresh.refreshToken())
        }
      } catch {
        // Non-critical — 401 retry path covers failures
      }
    },
  }
}

/** Вызывается из Nuxt plugin (enforce: pre) один раз на клиенте */
export function installApiTokenBridge(): void {
  if (!process.client) {
    setApiTokenBridge(null)
    return
  }

  setApiTokenBridge(createBridge())
}
